// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import wso2LogoBlack from '@src/assets/images/wso2-logo-black.svg';
import wso2LogoWhite from '@src/assets/images/wso2-logo-white.svg';

interface Props {
  error?: Error | null;
  onRetry?: () => void;
}

export default function AuthLoadingScreen({ error, onRetry }: Props) {
  const theme = useTheme();
  const logo = theme.palette.mode === 'light' ? wso2LogoBlack : wso2LogoWhite;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        bgcolor: 'background.default',
      }}
    >
      <Box
        component="img"
        src={logo}
        alt="WSO2"
        sx={{ width: 120, mb: 1 }}
      />
      {error ? (
        <>
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'error.main' }}>
            Sign-in failed. Please try again.
          </Typography>
          {onRetry && (
            <Button
              variant="contained"
              size="large"
              onClick={onRetry}
              sx={{
                mt: 1,
                bgcolor: '#f14e23',
                '&:hover': { bgcolor: '#c93d1b' },
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
              }}
            >
              Retry sign-in
            </Button>
          )}
        </>
      ) : (
        <>
          <LinearProgress
            sx={{
              width: 150,
              borderRadius: 1,
              '& .MuiLinearProgress-bar': { bgcolor: '#f14e23' },
            }}
          />
          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary' }}>
            Getting things ready...
          </Typography>
        </>
      )}
    </Box>
  );
}
